import {
  Form,
  FormInstance,
  Input,
  theme,
  Typography,
  DatePicker,
  Row,
  Switch,
  Col,
  Collapse,
  Button,
  message,
} from "antd";
import {
  DynamicControl,
  DynamicGroup,
  DynamicSchema,
} from "./DynamicForm.default";
import ComboBox from "./ComboBox";
import TextArea from "antd/es/input/TextArea";
import LabelBox from "./Label";
import { useEffect, useState } from "react";
import _ from "lodash";
import Editor from "./Editor";
import { hasValue } from "../utils/string";

export interface DynamicFormProps {
  readonly preview?: boolean;
  readonly form: FormInstance<any>;
  readonly schema?: DynamicSchema | null;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly onChange?: (value: any) => void;
}

function DynamicForm(props: DynamicFormProps) {
  const { token } = theme.useToken();
  const values = Form.useWatch([], props.form);
  const [schema, setSchema] = useState(props.schema ?? { groups: [] });
  const [messageApi, contextHolder] = message.useMessage();

  // Lifecycle

  useEffect(() => {
    if (props.schema) {
      setSchema({
        ...props.schema,
      });
    } else {
      setSchema({ groups: [] });
    }
  }, [props.schema]);

  useEffect(() => {
    if (schema) {
      setSchema({
        ...schema,
      });
    } else {
      setSchema({ groups: [] });
    }

    props.onChange?.(values);
  }, [values]);

  // Events

  function onBlur(control: DynamicControl, value: any): void {
    if ("format" in control && control.format) {
      props.form.setFieldValue(control.name, control.format(value));
    }
  }

  function onUploadFile(
    e: React.ChangeEvent,
    formProp: string,
    limit: number,
  ): void {
    const file = (e.target as any).files[0];
    if (file) {
      const mb = file.size / 1024 / 1024;
      if (mb > limit) {
        props.form.resetFields([formProp]);
        messageApi.open({
          type: "error",
          content: `File size limit is ${mb.toFixed(2)}mb`,
        });
      }
    }
  }

  return (
    <>
      <Form
        className={`my-form-item flex flex-col pb-1 ${props.className ?? "max-w-72"}`}
        form={props.form}
        layout={"vertical"}
        autoComplete="off"
        variant={"filled"}
        colon={false}
      >
        {schema.groups.map((group, index) => {
          const createGroup = (
            group: DynamicGroup,
            parentName?: string | number,
          ) => {
            const createdGroups = group.controls.map((control) => {
              const controlName =
                parentName != null ? [parentName, control.name] : control.name;
              const controlSize = control.size ?? "w-full";

              const formItemLabel = () => {
                const label = _.isFunction(control.label)
                  ? control.label(values || {})
                  : control.label;

                if (!label?.length) {
                  return undefined;
                }

                return (
                  <Typography.Text
                    className="text-sm"
                    style={{
                      color: token.colorTextDescription,
                    }}
                  >
                    {label}
                  </Typography.Text>
                );
              };

              if (props.preview || control.type === "labelbox") {
                const value = props.form.getFieldValue(controlName);
                if (!hasValue(value)) {
                  return <Col key={control.name} />;
                }

                return (
                  <Form.Item
                    key={control.name}
                    className={`m-0 ${controlSize}`}
                    name={controlName}
                  >
                    <LabelBox
                      className="mb-1 pl-2 pr-2"
                      layout={"vertical"}
                      name={
                        _.isFunction(control.label)
                          ? control.label(values || {})
                          : control.label
                      }
                      format={control.format}
                    />
                  </Form.Item>
                );
              }

              switch (control.type) {
                case "textbox":
                  return (
                    <Form.Item
                      key={control.name}
                      className={`m-0 p-1 ${controlSize}`}
                      name={controlName}
                      label={formItemLabel()}
                      rules={control.rules}
                    >
                      <Input
                        readOnly={control.readOnly}
                        onBlur={(e) => onBlur(control, e.target.value)}
                      />
                    </Form.Item>
                  );
                case "areabox":
                  return (
                    <Form.Item
                      key={control.name}
                      className={`m-0 p-1 ${controlSize}`}
                      name={controlName}
                      label={formItemLabel()}
                      rules={control.rules}
                    >
                      <TextArea
                        readOnly={control.readOnly}
                        rows={control.rows ?? 3}
                        onBlur={(e) => onBlur(control, e.target.value)}
                      />
                    </Form.Item>
                  );
                case "editorbox":
                  return (
                    <Form.Item
                      key={control.name}
                      className={`m-0 p-1 ${controlSize}`}
                      name={controlName}
                      label={formItemLabel()}
                      rules={control.rules}
                    >
                      <Editor
                        readOnly={control.readOnly}
                        rows={control.rows ?? 1}
                        onBlur={(value) => onBlur(control, value)}
                      />
                    </Form.Item>
                  );
                case "combobox":
                  return (
                    <Form.Item
                      key={control.name}
                      className={`m-0 p-1 ${controlSize}`}
                      name={controlName}
                      label={formItemLabel()}
                      rules={control.rules}
                    >
                      <ComboBox
                        disabled={control.disabled}
                        readOnly={control.readOnly}
                        request={control.request}
                      />
                    </Form.Item>
                  );
                case "rangepicker":
                  return (
                    <Form.Item
                      key={control.name}
                      className={`m-0 p-1 ${controlSize}`}
                      name={controlName}
                      label={formItemLabel()}
                      rules={control.rules}
                    >
                      <DatePicker.RangePicker
                        className="w-full"
                        readOnly={control.readOnly}
                      />
                    </Form.Item>
                  );
                case "rangetimepicker":
                  return (
                    <Form.Item
                      key={control.name}
                      className={`m-0 p-1 ${controlSize}`}
                      name={controlName}
                      label={formItemLabel()}
                      rules={control.rules}
                    >
                      <DatePicker.RangePicker
                        className="w-full"
                        readOnly={control.readOnly}
                        format={`YYYY-MM-DD ${control.timeFormat ?? "HH:mm"}`}
                        showTime={{
                          format: control.timeFormat ?? "HH:mm",
                          minuteStep: (control.timeMinutStep ??
                            undefined) as any,
                        }}
                      />
                    </Form.Item>
                  );
                case "datepicker":
                  return (
                    <Form.Item
                      key={control.name}
                      className={`m-0 p-1 ${controlSize}`}
                      name={controlName}
                      label={formItemLabel()}
                      rules={control.rules}
                    >
                      <DatePicker
                        className="w-full"
                        placeholder=""
                        readOnly={control.readOnly}
                      />
                    </Form.Item>
                  );
                case "datetimepicker":
                  return (
                    <Form.Item
                      key={control.name}
                      className={`m-0 p-1 ${controlSize}`}
                      name={controlName}
                      label={formItemLabel()}
                      rules={control.rules}
                    >
                      <DatePicker
                        className="w-full"
                        placeholder={
                          control.placeholder ? control.placeholder : ""
                        }
                        readOnly={control.readOnly}
                        format={`YYYY-MM-DD ${control.timeFormat ?? "HH:mm"}`}
                        showTime={{
                          format: control.timeFormat ?? "HH:mm",
                          minuteStep: (control.timeMinutStep ??
                            undefined) as any,
                        }}
                      />
                    </Form.Item>
                  );
                case "togglebox":
                  return (
                    <Row
                      key={control.name}
                      className={`flex flex-row items-center justify-between pl-1 ${controlSize}`}
                    >
                      {formItemLabel()}
                      <Form.Item
                        key={control.name}
                        className={`m-0 p-1`}
                        name={controlName}
                        valuePropName="checked"
                        rules={control.rules}
                      >
                        <Switch
                          checkedChildren={control.checkedText}
                          unCheckedChildren={control.unCheckedText}
                          disabled={control.readOnly}
                        />
                      </Form.Item>
                    </Row>
                  );
                case "pdf":
                  return (
                    <Form.Item
                      key={control.name}
                      className={`m-0 p-1 ${controlSize}`}
                      name={control.name}
                      label={formItemLabel()}
                      valuePropName="files"
                      rules={control.rules}
                    >
                      <Input
                        type="file"
                        readOnly={control.readOnly}
                        accept={control.accept}
                        onChange={(e) =>
                          onUploadFile(e, control.name, control.limit)
                        }
                      />
                    </Form.Item>
                  );
                case "numberbox":
                  return (
                    <Form.Item
                      key={control.name}
                      className={`m-0 p-1 ${controlSize}`}
                      name={controlName}
                      label={formItemLabel()}
                      rules={control.rules}
                    >
                      <Input
                        readOnly={control.readOnly}
                        type="number"
                        step={control.step}
                        min="0"
                        onBlur={(e) => onBlur(control, e.target.value)}
                      />
                    </Form.Item>
                  );

                default:
                  return <></>;
              }
            });

            return (
              <Row className="flex flex-row flex-wrap">{createdGroups}</Row>
            );
          };

          if (group.name && group.isList) {
            return (
              <Form.List
                key={index}
                name={group.name}
                rules={[
                  {
                    validator(_, value) {
                      if (
                        (value || []).some(
                          (value: any) => Object.keys(value).length === 0,
                        )
                      ) {
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                {(items, { add, remove }) => {
                  return (
                    <Col className="flex flex-col p-1">
                      <Collapse
                        className="my-collapse flex flex-col"
                        bordered={false}
                        accordion
                        defaultActiveKey={[0]}
                        items={items.map(({ key, name }, index) => {
                          const label = group.label
                            ? _.isFunction(group.label)
                              ? group.label(values || {}, index)
                              : group.label
                            : index;

                          return {
                            key,
                            label,
                            extra: (
                              <Row className="-mb-0.5 -mt-0.5 flex flex-row justify-end">
                                <Button
                                  type={"text"}
                                  disabled={items.length < 2}
                                  size="small"
                                  onClick={() => remove(index)}
                                >
                                  Remove
                                </Button>
                              </Row>
                            ),
                            children: createGroup(group, name),
                          };
                        })}
                      />
                      <Row className="mt-2 flex flex-row justify-start pl-2 pr-2">
                        <Button
                          onClick={() => {
                            add({});
                          }}
                        >
                          {`Add ${group.label}`}
                        </Button>
                      </Row>
                    </Col>
                  );
                }}
              </Form.List>
            );
          } else {
            const label = _.isFunction(group.label)
              ? group.label(values || {}, index)
              : group.label;
            return (
              <Form.Item
                key={group.name ?? index}
                label={!group.isList ? label : undefined}
                className={`m-0 p-0 ${group.size ?? "w-auto"}`}
              >
                {createGroup(group, group.name)}
              </Form.Item>
            );
          }
        })}
      </Form>
      {contextHolder}
    </>
  );
}

export default DynamicForm;
